import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

type Language = "EN" | "PL";

const DEFAULT_LANGUAGE: Language = "EN";
const SUPPORTED_LANGUAGES: readonly Language[] = ["EN", "PL"] as const;

const isLanguage = (value: unknown): value is Language =>
  typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

const getStoredLanguage = (): Language | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const saved = window.localStorage.getItem("language");
    if (isLanguage(saved)) {
      return saved;
    }
  } catch (error) {
    console.warn("Unable to read stored language preference", error);
  }

  return null;
};

const setStoredLanguage = (language: Language) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem("language", language);
  } catch (error) {
    console.warn("Unable to persist language preference", error);
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => unknown;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

const getTranslationValue = (language: Language, key: string): unknown => {
  const segments = key.split(".");
  let value: unknown = translations[language];

  for (const segment of segments) {
    if (value == null) {
      return undefined;
    }
    if (typeof value === "object" && segment in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }

  return value;
};

const translateKey = (language: Language, key: string): unknown => {
  const directValue = getTranslationValue(language, key);
  if (directValue !== undefined) {
    return directValue;
  }

  if (language !== DEFAULT_LANGUAGE) {
    const fallbackValue = getTranslationValue(DEFAULT_LANGUAGE, key);
    if (fallbackValue !== undefined) {
      return fallbackValue;
    }
  }

  return key;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => getStoredLanguage() ?? DEFAULT_LANGUAGE);

  useEffect(() => {
    setStoredLanguage(language);
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    if (!isLanguage(lang)) {
      console.warn(`Unsupported language "${lang}" requested. Falling back to ${DEFAULT_LANGUAGE}.`);
      setLanguageState(DEFAULT_LANGUAGE);
      return;
    }
    setLanguageState(lang);
  }, []);

  const t = useCallback((key: string): unknown => translateKey(language, key), [language]);

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

const translations = {
  EN: {
    nav: {
      creators: "Creators",
      companies: "Companies",
      portfolio: "Portfolio",
      about: "About Us",
      logout: "Logout",
      menu: "Menu",
      adminLogin: "Admin Login",
    },
    meta: {
      indexTitle: "Zendizz - Redefining Growth with AI",
      indexDescription:
        "AI-driven marketing agency helping creators and brands scale with authentic UGC, automated content and data-powered campaigns. Elevate growth in the new era of marketing.",
    },
    hero: {
      title: "Content that makes noise —",
      titleSecondLine: "and drives",
      ai: "results",
      subtitle: "We connect brands with authentic creators who fit their identity. AI speeds up the matching and supports content creation that stays true to your brand’s DNA.",
      forCreators: "For Creators",
      forCompanies: "For Companies",
      weGrowTogether: "We grow together",
    },
    valueProp: {
      title: "The Future of Marketing is Authentic & Autonomous",
      subtitle: "Zendizz is the premier AI marketing agency connecting brands with authentic UGC creators. We fuse human creativity with content automation to drive scalable brand growth.",
      creators: {
        title: "For Creators",
        desc: "Monetize your unique voice. We match you with brands that align with your values using deep persona analysis. Whether it's flexible UGC campaigns or dedicated brand ambassador roles, build a sustainable career without the chaos of cold DMs.",
        cta: "Join Creator Network"
      },
      brands: {
        title: "For Brands",
        desc: "Scale trust with autonomous precision. Our AI identifies the perfect micro-influencers for your niche using deep persona matching. Turn autonomous content creation into a high-performance growth engine that drives real engagement and measurable ROI.",
        cta: "Scale Your Brand"
      },
      automation: {
        title: "AI Automation & Deep Matching",
        desc: "Where precision meets scalability. Our autonomous systems produce on-brand content — from educational pieces to social posts — guided by detailed persona insights so it feels genuinely human. We also automate creator–brand matching to pinpoint the ideal niche fit, keeping your presence active around the clock. With analytics and content creation handled, you stay focused on strategy and growth.",
        cta: "Explore AI Solutions"
      },
      cta: {
        title: "Ready to Scale to the Next Level?",
        desc: "Join where data truly fuels creativity. Stop guessing and start growing with the Zendizz ecosystem that bridges the needs of creators and brands.",
        brandBtn: "Start as a Brand",
        creatorBtn: "Join as a Creator"
      }
    },
    howItWorks: {
      title: "Finding the Perfect Match Between Your Brand and the Market",
      step1Title: "Deep Persona Research",
      step1Description:
        "We analyze transcripts, articles, and YouTube videos to uncover key narratives, underlying frameworks, and the core motivations that shape your world.",
      step2Title: "Comprehensive Brand Report",
      step2Description:
        "We create a Persona Report that defines your distinctive tone, perspective, and long-term objectives — a foundation for building authentic and aligned content.",
      step3Title: "AI Matching & Content Automation",
      step3Description:
        "Using the report, our system automatically matches the brand with the most relevant micro-influencers for UGC campaigns and generate automated, brand-aligned content.",
      closing:
        "Unlike typical AI-generated content, our solution produces content indistinguishable from what your company already creates — authentic, on-brand, and scalable.",
      comparisonTitle: "Why UGC with Micro-Influencers is Better?",
      authenticity: "Authenticity & Trust",
      authenticityDesc:
        "Micro-influencers maintain closer relationships with their audience, resulting in more authentic recommendations that feel like advice from a friend rather than a paid promotion.",
      niche: "Niche Expertise",
      nicheDesc:
        "Micro-influencers often specialize in specific niches, allowing brands to reach highly targeted audiences with relevant interests instead of broad, unfocused reach.",
      budget: "Budget Efficiency",
      budgetDesc:
        "Instead of spending your entire budget on one celebrity influencer, work with 10-20 micro-influencers to diversify risk, test different markets, and achieve better overall ROI.",
      engagement: "Real Engagement vs Vanity Metrics",
      engagementDesc:
        "Macro-influencers often have inflated follower counts with low engagement. Micro-influencers deliver actual conversations, comments, and conversions from genuinely interested audiences.",
      viral: "VIRAL POTENTIAL",
      viralDesc:
        "Authentic content from micro-creators can go viral just like big influencer content, exponentially amplifying your brand's reach and creating organic growth momentum.",
    },
    creators: {
      heroTitle: "Monetize Your Creativity. Consistently.",
      heroDescription: "Join the Zendizz ecosystem. We match you with brands that fit your unique style using AI, or hire you as the permanent face for our clients.",
      heroButton: "Apply to Creator Base",
      whyZendizz: "Why Zendizz?",
      whySubtitle: "No more bad barter deals or DM chaos.",
      valueProps: {
        matchTitle: "Matched via Deep Persona Analysis",
        matchDesc: "Our algorithm analyzes your style and audience. You only receive gigs you genuinely \"feel.\" No promoting steak if you're a vegan.",
        pathsTitle: "Two Career Paths",
        pathsDesc: "Choose between single UGC gigs or a fixed contract as a Dedicated Creator, where you run a brand's profile for a fixed monthly rate.",
        rulesTitle: "Clear Rules",
        rulesDesc: "Clear briefs, on-time payments, and agency support. We negotiate fair rates with the client, so you can focus on creating."
      },
      chooseMode: "Choose Your Mode",
      modeSubtitle: "How do you want to work?",
      modes: {
        ugc: {
          title: "Project-Based Collaboration (UGC Campaigns)",
          subtitle: "For creators seeking flexibility and project variety.",
          targetProfile: "Creators prioritizing variety and possessing a highly engaged personal community.",
          coreDeliverable: "Production of high-performing, authentic video content for use on your own channels or the client’s paid/organic media.",
          compensationModel: "Project-based remuneration (pay-per-asset) with opportunities for product sampling.",
          button: "Apply for Campaigns"
        },
        dedicated: {
          title: "Brand Ambassadorship (Dedicated Creator Retainer)",
          subtitle: "For creators seeking stability and professional separation of their personal brand.",
          targetProfile: "Creators prioritizing stable, predictable monthly income who wish to separate commercial work from their personal brand presence.",
          coreDeliverable: "Serve as the exclusive, consistent on-camera talent, generating scheduled content for the client's owned media channels.",
          compensationModel: "Guaranteed monthly retainer fee and long-term contract stability.",
          button: "Apply for Retainer Roles"
        }
      },
      requirements: {
        title: "Who are we looking for?",
        subtitle: "We don't just look at numbers. We look at Talent. You don't need a million followers. We are looking for creators who:",
        list: [
          "Know how to tell a story (storytelling) and hold attention.",
          "Understand the aesthetics of TikTok/Reels (editing, lighting, pacing).",
          "Are punctual (this is key for us in B2B).",
          "Are authentic in specific niches (Beauty, Tech, Finance, Parenting, Gaming, and others)."
        ]
      },
      faq: {
        title: "FAQ",
        subtitle: "Short and sweet.",
        list: [
          {
            q: "Is this paid?",
            a: "Yes. We focus mainly on paid campaigns. Barter is only an add-on for smaller starter gigs."
          },
          {
            q: "Do I have to show my face?",
            a: "In 90% of cases, yes – that is the core of trust in UGC."
          },
          {
            q: "What is Deep Persona Analysis?",
            a: "It is our proprietary technology that checks your niche, demographics, and audience engagement to match you only with brands where you will deliver high performance (and client satisfaction)."
          }
        ]
      },
      generalTalentPool: {
        title: "Open to Both Opportunities?",
        description: "If you are flexible between project-based campaigns and a dedicated retainer role, apply to our General Talent Pool. We will review your profile and match you with the best fit based on current client demand.",
        button: "Apply for General Consideration"
      },
    },
    companies: {
      title: "Scale Trust. Automate Reach.",
      subtitle: "We're not another influencer marketing agency sending you a list of random people. We're your technology partner. We build autonomous customer acquisition channels for you.",
      chooseStrategy: "Choose a strategy tailored to your growth stage:",
      getStarted: "Book a Meeting",
      coreProcess: {
        title: "From Data to Domination.",
        subtitle: "We don't guess. Our process transforms analytics into content that converts.",
        steps: [
          {
            title: "Deep Persona Analysis (The Brain)",
            description: "We analyze transcripts, articles, videos, and competitor materials to uncover key narratives, mental frameworks, and core motivations that shape your customer's world. We understand \"why\" they buy before we start selling."
          },
          {
            title: "Comprehensive Brand Report (The Strategy)",
            description: "We create a \"Persona Report\" that defines your unique Tone of Voice, perspective, and brand goals. This is the foundation ensuring every video and post – whether created by humans or AI – is consistent with your DNA."
          },
          {
            title: "AI Matching & Automation (The Execution)",
            description: "Using the report, the system automatically matches ideal creators OR generates autonomous content. AI Content: We create content indistinguishable from senior copywriter work – authentic, brand-aligned, and infinitely scalable. Human Match: We connect you with micro-influencers who naturally fit your analysis results."
          }
        ]
      },
      growthEngines: {
        title: "One ecosystem. Three ways to grow.",
        subtitle: "Choose the tool matched to your goals.",
        engines: [
          {
            title: "AI Autonomous Growth",
            subtitle: "For companies seeking scale and authority.",
            description: "Dominate your informational niche. Our systems generate and publish slides, articles, X threads, and LinkedIn posts. Build expert positioning 24/7, without spending your time.",
            benefit: "Scalability and omnipresence."
          },
          {
            title: "Campaign UGC",
            subtitle: "For companies needing trust and ad materials.",
            description: "Authentic video from verified micro-influencers. Perfect fuel for your paid campaigns (Ads) and social media to build Social Proof.",
            benefit: "High conversion and diverse faces."
          },
          {
            title: "Dedicated Creators",
            subtitle: "For companies seeking stability and a \"Brand Face\".",
            description: "Instead of one-off collaborations – a permanent exclusive creator (Retainer). Builds relationships with your community on your channels, ensuring communication consistency for months.",
            benefit: "Audience loyalty and predictability."
          }
        ]
      },
      whyItWorks: {
        title: "Why does authenticity beat reach?",
        subtitle: "Whether you choose AI or Humans – the rules of the game are the same.",
        points: [
          {
            title: "Authenticity and Trust",
            description: "Micro-communication builds closer relationships. Content (whether creator video or AI expert thread) sounds like advice from a partner, not a corporate message."
          },
          {
            title: "Right Niche",
            description: "Thanks to Deep Persona Analysis, we reach only highly targeted audiences. You don't pay for empty views, but for potential customer attention."
          },
          {
            title: "Efficient Budget Management",
            description: "Instead of burning budget on one expensive celebrity or PR agency, you diversify. Test different markets and narratives, achieving better overall ROI."
          },
          {
            title: "Real Engagement vs Empty Numbers",
            description: "No more ego pumping. We deliver real conversations, comments, and conversions from genuinely interested audiences."
          },
          {
            title: "Viral Potential",
            description: "Good content speaks for itself. Authentic messaging has a chance to go viral regardless of account size, creating free growth momentum for your brand."
          }
        ]
      },
      packages: {
        humanTrust: {
          title: "Real Influence",
          tagline: "Establish credibility through authentic creator partnerships and high-converting video content.",
          description: "Scope of Services:",
          features: [
            "Data-Driven Creator Matching: Creator selection based on hard demographic data and niche analysis, not \"gut feeling\".",
            "High-Fidelity Video Production: Production of video materials (Reels/TikTok/Shorts) with high conversion potential, ready for distribution.",
            {
              title: "Two execution paths:",
              subFeatures: [
                "Scalable Campaigns: One-time, broad campaigns with one or multiple creators to diversify messaging and test advertising angles.",
                "Dedicated Creators (Retainer): Assignment of exclusive brand ambassador(s) who conduct consistent communication and become the \"face\" of the company long-term."
              ]
            },
            "Full Commercial Rights: Complete transfer of copyrights and licenses for use in advertising (Ads).",
            "Performance Tracking: Monitoring material effectiveness and reporting key metrics.",
            "Strategic Onboarding: Dedicated onboarding process and ongoing Account Manager support."
          ],
          goal: "Drive trust, social proof, and measurable conversions.",
          idealFor: "E-commerce, D2C brands, mobile applications.",
          pricingCategories: [
            {
              category: "Campaign UGC",
              options: [
                { label: "UGC Creators", price: "starts from $299" }
              ]
            },
            {
              category: "Dedicated Creators",
              options: [
                { label: "Dedicated UGC Creators", price: "starts from $699" }
              ]
            }
          ],
          button: "Choose"
        },
        aiGrowth: {
          title: "Autonomous Content Ecosystem (AI)",
          tagline: "Solution for companies pursuing omnichannel presence and building thought leadership position with minimal time investment.",
          description: "Scope of Services:",
          features: [
            "AI-Driven Authentic Content: Generation of substantive content (educational slides, X threads, LinkedIn/Blog articles) maintaining authenticity and consistency with brand Tone of Voice.",
            "Omnichannel Distribution: Simultaneous publication and distribution of content across all key platforms.",
            "Strategic Community Engagement: Supervised interaction system with industry accounts for organic traffic acquisition.",
            "Niche Theme Pages: Optional creation and management of external thematic profiles, building a market around your niche (direct sales support).",
            "Continuous Optimization: Constant calibration of content strategy based on incoming engagement data."
          ],
          goal: "Build authority, educate your market, drive organic growth.",
          idealFor: "SaaS, B2B, professional services, fintech.",
          pricingCategories: [
            {
              category: "Pricing",
              options: [
                { label: "Presence Package (Daily posts on 1 platform)", price: "from $399" },
                { label: "Authority Package (Omnichannel + Blog + Engagement)", price: "from $1599" }
              ]
            }
          ],
          button: "Choose"
        },
        hyperGrowth: {
          title: "Holistic Growth Hacking (Hybrid)",
          tagline: "Complete solution: human authenticity meets AI scale for market dominance.",
          description: "Scope of Services:",
          features: [
            "Full Human + AI Synergy: Integrated strategy using video for relationship building and AI content for market education and SEO.",
            "Advanced Data Optimization: Continuous A/B testing and strategy optimization in real-time (Real-Time Data).",
            "External Marketing Department: Taking full responsibility for creative and distribution processes.",
            "Priority Support: Dedicated Project Manager with priority availability for the client.",
            "Comprehensive ROI Reporting: Advanced conversion analytics broken down by channels and formats."
          ],
          goal: "Your fully outsourced marketing department.",
          idealFor: "Growth-stage companies ready for market leadership.",
          pricingCategories: [
            {
              category: "Pricing",
              options: [
                { label: "Hyper Growth (Long-term contract)", price: "Custom pricing" }
              ]
            }
          ],
          button: "Choose"
        }
      },
      process: {
        title: "How We'll Do It",
        steps: [
          { title: "Brief & Analysis", description: "You fill out a questionnaire, we launch Deep Persona Analysis." },
          { title: "Matching / Setup", description: "We select a Creator (Human) or configure AI." },
          { title: "Review", description: "You approve the strategy and first batch of materials." },
          { title: "Launch", description: "We start. You receive ready files or we publish them for you." },
          { title: "Scale", description: "We analyze results and push what works." }
        ]
      },
      faq: {
        title: "Frequently Asked Questions",
        list: [
          { q: "Do I have control over what creators publish?", a: "Yes. In the \"Dedicated Creator\" and \"Campaign UGC\" models, you have full visibility into materials before publication. Nothing goes out without your approval (unless you give us free rein)." },
          { q: "How is a \"Dedicated Creator\" different from a regular influencer?", a: "An influencer promotes you on their channel (alongside 10 other brands). A Dedicated Creator creates content for you, on your channels. They work like your virtual employee on a permanent contract." },
          { q: "Is AI content safe for brand image?", a: "Yes. Our AI doesn't run unsupervised. Every strategy is calibrated by a human, and content goes through Tone of Voice compliance verification." }
        ]
      },
      cta: {
        title: "Ready for marketing that works?",
        description: "We don't sell \"posts\". We sell a customer acquisition system. Consult your niche with us.",
        button: "Book a Meeting"
      },
      ugcDialog: {
        title: "Why UGC & Micro-Influencers?",
        intro:
          "User-generated content (UGC) and micro-influencers are revolutionizing digital marketing. Here's why they're more effective than traditional influencer marketing:",
        authenticity: "Authenticity Over Reach",
        authenticityDesc: "Smaller creators are more authentic. Their audience trusts them more than celebrities.",
        engagement: "Higher Engagement",
        engagementDesc: "Micro-influencers often generate 3-5x more interactions compared to big names.",
        roi: "Better ROI",
        roiDesc: "Instead of paying one big fee, you get dozens of authentic content pieces that drive conversions.",
        scalable: "Scalable & Diverse Content",
        scalableDesc: "More creators = more styles, more voices, more trust.",
        niche: "Niche Targeting",
        nicheDesc:
          "Small creators speak directly to tight-knit communities (fitness, gaming, lifestyle), so your brand reaches exactly where it should.",
        viral: "Viral Potential",
        viralDesc:
          "Authentic content from small creators can go viral just like big influencer content, amplifying your brand's reach exponentially.",
        conclusion: "👉 UGC with micro-influencers isn't just content. It's conversion.",
        whyZendizz: "Why Choose Zendizz?",
        reviewedCreators: "Reviewed & Classified Creators:",
        reviewedCreatorsDesc: "Every creator in our network is vetted and categorized for quality assurance.",
        perfectMatching: "Perfect Matching:",
        perfectMatchingDesc:
          "Our algorithm connects you with creators whose audience perfectly aligns with your brand.",
        endToEnd: "End-to-End Management:",
        endToEndDesc: "From creator selection to content delivery and performance tracking, we handle everything.",
        getStartedUgc: "Get Started with UGC",
      },
      ugc: {
        title: "User-Generated Content & Influencer Marketing",
        description: "UGC campaigns and creator collaborations",
        detail1: "Authentic user-generated content",
        detail2: "Strategic influencer partnerships",
        detail3: "Brand storytelling at scale",
        button: "Start UGC Campaign",
        specialization: "OUR SPECIALIZATION",
      },
      aiContent: {
        title: "AI Content",
        description: "Fast AI video/image production with editing",
        detail1: "AI-powered video creation",
        detail2: "Smart image generation",
        detail3: "Automated content editing",
        button: "Generate AI Content",
      },
      performance: {
        title: "Performance Marketing",
        description: "TikTok/Meta/Google Ads, SEO/SEM light",
        detail1: "Multi-platform ad campaigns",
        detail2: "ROI-focused optimization",
        detail3: "Search engine marketing",
        button: "Launch Marketing",
      },
      automation: {
        title: "AI Automation",
        description: "Make/Zapier automations, integrations, dashboards",
        detail1: "Workflow automation",
        detail2: "System integrations",
        detail3: "Custom dashboards",
        button: "Automate Workflows",
      },
      intelligence: {
        title: "Market Intelligence",
        description: "Reports, insights, competitor & trend analysis",
        detail1: "Competitive analysis",
        detail2: "Market trend reports",
        detail3: "Strategic insights",
        button: "Get Insights",
      },
      productStrategy: {
        title: "Product Strategy",
        description: "Offer design, copy, pricing and margins",
        detail1: "Product positioning",
        detail2: "Pricing optimization",
        detail3: "Strategic planning",
        button: "Optimize Strategy",
      },
      extraServices: "Extra Services",
      websites: {
        title: "Websites & E-commerce",
        description: "Shopify stores or custom websites with marketing integrations",
        detail1: "Custom website development",
        detail2: "E-commerce solutions",
        detail3: "Marketing integrations",
        button: "Build Website",
      },
      growthExp: {
        title: "Growth & Experimentation",
        description: "Funnel analysis, A/B testing, automation, retention",
        detail1: "Conversion optimization",
        detail2: "A/B testing framework",
        detail3: "Growth automation",
        button: "Start Testing",
      },
      whyChoose: "Why Choose Zendizz?",
      lightningFast: "Lightning Fast",
      lightningFastDesc: "Rapid deployment and execution.",
      provenResults: "Proven Results",
      provenResultsDesc: "Data-driven strategies that deliver.",
      aiPowered: "AI-Powered",
      aiPoweredDesc: "Cutting-edge technology solutions.",
      expertTeam: "Expert Team",
      expertTeamDesc: "Dedicated professionals for your success.",
      howItWorks: "How It Works",
      consultation: "Consultation",
      consultationDesc: "We understand your business goals.",
      strategy: "Strategy",
      strategyDesc: "Custom solutions tailored for you.",
      growth: "Growth",
      growthDesc: "Watch your business scale and succeed.",
    },
    about: {
      title: "About Us",
      intro:
        "At Zendizz, we connect creators with brands through intelligent matchmaking powered by artificial intelligence. Our platform was created to connect micro-influencers with companies seeking authentic, engaging content, creating partnerships that truly drive growth for both sides.",
      content1:
        "We're not another marketplace. Zendizz is a comprehensive ecosystem where UGC campaigns meet advanced AI technology, performance marketing, and strategic brand development. Whether it's matching creators to the right brands, scaling reach through authentic partnerships, or automatically generating content indistinguishable from what you've been creating so far— we make growth seamless, measurable, and sustainable.",
      content2:
        "Our AI technology analyzes creator profiles, matches them with brands with aligned values and audiences, and optimizes every collaboration and generated content for maximum impact.",
      content3:
        "For brands, this means access to creators and content that truly resonate with their message. For creators — opportunities that perfectly fit their niche and support career development.",
      content4:
        "At Zendizz, we don't just facilitate collaborations — we're building a community where brands and creators grow together, supported by technology that makes every partnership and every piece of content smarter, more authentic, and more effective.",
      quote: "Together, we empower creators and brands to grow as one.",
      founderName: "Blaze, Founder of Zendizz",
      certification: "Certified in AI for Business Development",
      coreValues: "Our Core Values",
      authenticity: "Authenticity",
      authenticityDesc: "We believe in genuine connections and real relationships between creators and brands.",
      innovation: "Innovation",
      innovationDesc: "Leveraging AI and cutting-edge technology to create smarter, more effective campaigns.",
      growth: "Growth",
      growthDesc: "Dedicated to accelerating results and helping our partners scale faster than ever.",
    },
    footer: {
      copyright: "© 2025 Zendizz. Redefining growth with AI.",
      privacyPolicy: "Privacy Policy",
    },
    portfolio: {
      title: "Our Work",
      subtitle: "From UGC campaigns to full-scale digital transformation — see what we create for creators and brands.",
      loading: "Loading portfolio items...",
      ctaTitle: "Ready to Create Something Amazing?",
      ctaSubtitle: "Let's collaborate and bring your vision to life with our full-spectrum marketing solutions.",
      ctaButton: "Start Your Project",
      sections: {
        ugc: {
          title: "UGC & Influencer Campaigns",
          description: "Authentic creator collaborations that drive real engagement and conversions.",
        },
        aiContent: {
          title: "AI-Driven Content Production",
          description: "Cutting-edge content creation powered by artificial intelligence.",
        },
        webDesign: {
          title: "Web Design & Development",
          description: "Beautiful, high-converting websites built with modern technology.",
        },
        ecommerce: {
          title: "E-Commerce Solutions",
          description: "Complete online stores that maximize sales and customer experience.",
        },
        performance: {
          title: "Performance Marketing",
          description: "Data-driven campaigns that deliver measurable ROI.",
        },
        brand: {
          title: "Brand Strategy & Positioning",
          description: "Strategic guidance to help brands stand out and scale.",
        },
      },
    },
    creatorForm: {
      title: "Creator Application",
      subtitle: "Complete your creator application by following the steps below.",
      step1Title: "Select Your Platforms",
      step1Subtitle: "Choose the platforms where you create content",
      step2Title: "Content Categories",
      step2Subtitle: "Select 1-5 categories that describe your content",
      step3Title: "Contact Information",
      step3Subtitle: "Tell us how to reach you",
      platformsSectionTitle: "Choose Your Platform",
      platformsSectionDesc: "Select the platforms you are most active on.",
      verificationSectionTitle: "Verification",
      verificationSectionDesc: "Complete the verification to submit your application.",
      selectPlatform: "Select at least one platform",
      followers: "Followers",
      averageViewers: "Average Viewers",
      platformLink: "Platform Link (optional)",
      name: "Full Name",
      email: "Email",
      country: "Country",
      selectCountry: "Select your country",
      other: "Other",
      maxCategories: "Select up to 5 categories",
      categoryOther: "Specify other category",
      back: "Back",
      next: "Next",
      submit: "Submit Application",
      submitting: "Submitting...",
      qualified: "Congratulations!",
      qualifiedMessage: "Thank you! You qualify for further evaluation. We'll be in touch!",
      notQualified: "Keep Growing!",
      notQualifiedMessage:
        "The system has determined that you don’t yet meet all of our criteria. Don’t worry — we also review applications manually, so nothing is set in stone ;)",
      close: "Close",
      errorPlatform: "Please select at least one platform",
      errorCategories: "Please select at least 1 content category",
      errorMaxCategories: "Please select no more than 5 content categories",
      errorContactInfo: "Please provide name, email, and country",
      errorEmail: "Please provide a valid email address",
    },
    companyForm: {
      title: "Service Request",
      step1Title: "What's your main goal?",
      step1Subtitle: "Choose the primary objective for this project",
      step2Title: "Choose your package",
      step2Subtitle: "Select the service that best fits your needs",
      step3Title: "Contact Information",
      step3Subtitle: "Tell us about your company",
      goals: {
        createContent: "Create content",
        workCreators: "Work with creators",
        scalePerformance: "Scale performance",
        automateIntegrate: "Automate & integrate",
        understandMarket: "Understand the market",
        buildWebsite: "Build website / store",
      },
      packages: {
        aiContent: "AI Content",
        ugcInfluencers: "User-Generated Content & Influencer Marketing",
        performanceMarketing: "Performance Marketing",
        websitesEcommerce: "Websites & E-commerce",
        aiAutomation: "AI Automation",
        marketIntelligence: "Market Intelligence",
        ugcPackage: "Human Trust & Conversion",
        ugcPackageDesc:
          "Establish credibility through authentic creator partnerships and high-converting video content.",
        aiAutomationPackage: "AI Autonomous Growth",
        aiAutomationPackageDesc:
          "Dominate your niche with AI-powered content that positions you as the industry authority.",
        fullPackage: "Hyper Growth Hack (Hybrid)",
        fullPackageDesc:
          "The complete solution: human authenticity meets AI scale for market dominance.",
      },
      step1ChoosePath: "Choose Your Growth Path",
      step1SelectPackage: "Select the perfect package to accelerate your business growth",
      getStarted: "Get Started",
      collaborationType: "Type of collaboration",
      ugcLabel: "Campaign UGC (Freelance)",
      ugcDescription: "One-off campaigns for ads & content volume.",
      influencerLabel: "Dedicated Creator (Retainer)",
      influencerDescription: "Long-term brand face & exclusive partnership.",
      selectCategories: "Select content categories",
      selectPlatforms: "Select platforms",
      selectCountry: "Select your country",
      fullName: "Full Name",
      companyName: "Company Name",
      email: "Email",
      website: "Website (optional)",
      projectDescription: "Project Description (optional)",
      estimatedBudget: "Estimated Budget (optional)",
      addGrowthExp: "Add Growth & Experimentation",
      back: "Back",
      next: "Next",
      submit: "Submit Request",
      submitting: "Submitting...",
      successTitle: "Application submitted successfully!",
      successMessage: "We'll be in touch with you soon.",
      matchingCreators: "We found {count} qualified creator{s} matching your criteria!",
      ugcInfoTitle: "Why UGC with Small Creators Works Better than Big Influencers",
      continueButton: "Continue to Application",
      ugcBenefits: {
        authenticity: "Authenticity over reach",
        authenticityDesc: "Smaller creators feel real. Their audience trusts them more than celebrity endorsements.",
        engagement: "Higher engagement",
        engagementDesc: "Micro-influencers often drive 3–5x more interactions compared to big names.",
        roi: "Better ROI",
        roiDesc: "Instead of paying one big fee, you get dozens of authentic pieces of content that convert.",
        scalable: "Scalable & diverse content",
        scalableDesc: "More creators = more styles, more voices, more trust.",
        niche: "Niche targeting",
        nicheDesc:
          "Small creators speak directly to tight communities (fitness, gaming, lifestyle), so your brand lands exactly where it should.",
        viral: "Viral potential",
        viralDesc:
          "Small creators' authentic content can go viral just like big influencers', amplifying your brand reach exponentially.",
        conclusion: "👉 UGC with micro-influencers isn't just content. It's conversion.",
      },
      creatorCollabTitle: "Creator Collaboration",
      creatorCollabDesc: "Tell us what type of creators you're looking for",
      collaborationTypeTitle: "Type of collaboration",
      collaborationTypeDesc: "Choose the type of collaboration that fits your needs",
      ugcCollabDesc: "Short-term campaigns showcasing your product or service from a customer's perspective.",
      influencerCollabDesc: "Through regular campaigns, your brand gains audience loyalty and consistent growth in recognition.",
      contentCategoriesTitle: "Content Categories",
      contentCategoriesDesc: "Select up to 5 categories that match your target audience",
      selectCategoriesDesc: "Select up to 5 content categories ({selected}: {count}/5)",
      selectPlatformsTitle: "Select platforms",
      selectPlatformsDesc: "Select the platforms where you want content",
      cancel: "Cancel",
      continueToForm: "Continue to Form",
      primaryGoal: "What's your primary goal?",
      primaryGoalDesc: "Choose the option that best describes your main objective",
      recommendedPackage: "Recommended package",
      basedOnGoal: "Based on your goal, we recommend",
      alternative: "Alternative",
      selectPackage: "Select your package",
      selectPackageDesc: "Choose the service that best fits your needs",
      creatorCollabSetup: "Creator Collaboration Setup",
      findMatchingCreators: "Find Matching Creators",
      nextStep: "Next Step",
      letsConnect: "Let's connect!",
      shareDetails: "Share your details so we can tailor the perfect solution for your business",
      contactInfo: "Contact Information",
      howReach: "How should we reach you?",
      companyInfo: "Company Information",
      tellMore: "Tell us more about your business",
      projectDetails: "Project Details",
      projectDetailsDesc: "Help us understand your needs (optional)",
      describeProject: "Describe your project",
      projectPlaceholder: "Share your goals, challenges, and what you hope to achieve...",
      estimatedBudgetLabel: "Estimated Budget",
      selectBudget: "Select a budget range",
      budgetOption1: "Less than $5,000",
      budgetOption2: "$5,000 - $15,000",
      budgetOption3: "$15,000 - $50,000",
      budgetOption4: "$50,000+",
      budgetOption5: "Not sure yet",
      perfectMatch: "Perfect! We found {count} creator{s} who match your requirements.",
      expectEmail: "Expect an email from us within 24 hours with creator profiles and next steps.",
      noMatch: "We don't currently have creators matching your exact criteria, but don't worry!",
      activeSearch:
        "Our team will actively search for creators that fit your needs and reach out within 48 hours with potential matches.",
      collaboration: "Collaboration",
      categoriesLabel: "Categories",
      platforms: "Platforms",
      selected: "Selected",
      specifyCategory: "Please specify your category (up to 25 words):",
      describePlaceholder: "Describe your niche category...",
      words: "words",
      categories: {
        fitness: "Fitness",
        gaming: "Gaming",
        lifestyle: "Lifestyle",
        fashion: "Fashion",
        beauty: "Beauty",
        tech: "Tech",
        education: "Education",
        finance: "Finance & Business",
        travel: "Travel",
        food: "Food & Cooking",
        music: "Music",
        art: "Art & Design",
        photography: "Photography",
        self_improvement: "Self Improvement",
        parenting: "Parenting & Family",
        pets: "Pets & Animals",
        automotive: "Automotive",
        outdoors: "Outdoors",
        diy: "DIY & Making",
        home: "Home & Garden",
        comedy: "Comedy",
        books: "Books",
        science: "Science",
        crypto: "Crypto & Web3",
        other: "Other",
      },
    },
    // Toast messages
    toast: {
      logoutSuccess: "Logged out successfully",
      applicationSubmitted: "Application submitted successfully!",
      foundCreators: "We found {count} qualified creator{plural} matching your criteria!",
      contactSoon: "We'll be in touch with you soon.",
      submissionFailed: "Submission failed",
      tryAgainLater: "Please try again later.",
    },
    // Success screen messages
    successScreen: {
      submissionSent: "Submission Sent!",
      thankYou: "Thank you for your submission. We'll review your request and contact you within 24 hours.",
      submissionFailedTitle: "Submission Failed",
      submissionFailedDesc: "An error occurred while submitting. Please try again.",
      tryAgain: "Try Again",
    },
  },
  PL: {
    // Navbar
    nav: {
      creators: "Twórcy",
      companies: "Firmy",
      portfolio: "Portfolio",
      about: "O Nas",
      logout: "Wyloguj",
      menu: "Menu",
      adminLogin: "Logowanie Admina",
    },
    meta: {
      indexTitle: "Zendizz - Przedefiniowujemy Rozwój z AI",
      indexDescription: "Agencja marketingowa, która podnosi twórców i marki dzięki sile sztucznej inteligencji. Dołącz do nas, aby przedefiniować rozwój.",
    },
    hero: {
      title: "Kontent, który słychać —",
      titleSecondLine: "i przynosi",
      ai: "rezultaty",
      subtitle: "Łączymy marki z autentycznymi twórcami, którzy pasują do ich tożsamości. Sztuczna inteligencja przyspiesza dopasowywanie i wspiera tworzenie treści, które pozostają spójne z DNA Twojej marki.",
      forCreators: "Dla Twórców",
      forCompanies: "Dla Firm",
      weGrowTogether: "Rozwijamy się razem",
    },
    valueProp: {
      title: "Przyszłość Marketingu jest Autentyczna i Autonomiczna",
      subtitle: "Zendizz to agencja marketingu AI, która łączy marki z autentycznymi twórcami UGC. Integrujemy ludzką kreatywność z automatyzacją treści, tworząc skalowalne działania, które realnie wzmacniają rozwój marki.",
      creators: {
        title: "Dla Twórców",
        desc: "Zarabiaj na swoim stylu i sposobie opowiadania. Łączymy Cię z markami, które naprawdę pasują do Twojej osobowości i wartości — dzięki analizie persony opartej na danych. Niezależnie od tego, czy tworzysz UGC, czy szukasz długofalowych współprac jako ambasador, możesz rozwijać stabilną karierę bez konieczności wysyłania setek cold e-maili.",
        cta: "Dołącz do Sieci Twórców",
      },
      brands: {
        title: "Dla Marek",
        desc: "Buduj zaufanie i skaluj wyniki z precyzją opartą na AI. Nasze systemy identyfikują twórców i mikro-influencerów idealnie dopasowanych do Twojej niszy. Zamień autonomiczne tworzenie treści w wydajny silnik wzrostu, który generuje prawdziwe zaangażowanie i mierzalne ROI.",
        cta: "Skaluj Swoją Markę",
      },
      automation: {
        title: "Automatyzacja AI i Głębokie Dopasowanie",
        desc: "Miejsce, w którym precyzja spotyka skalowalność. Nasze autonomiczne modele tworzą treści zgodne z Twoją marką — od artykułów edukacyjnych po posty społecznościowe — wykorzystując dogłębne dane o odbiorcach, dzięki czemu brzmią jak te tworzone przez ludzi. Automatycznie dopasowujemy twórców do marek, wskazując najlepsze niszowe połączenia i utrzymując stałą, 24/7 obecność online. A dzięki automatycznej analityce i produkcji treści możesz skupić się na strategii i rozwoju.",
        cta: "Poznaj Rozwiązania AI",
      },
      cta: {
        title: "Gotowy wejść na wyższy poziom skalowania?",
        desc: "Dołącz do miejsca, gdzie dane realnie wspierają kreatywność. Zamiast zgadywać, rozwijaj markę dzięki ekosystemowi Zendizz, który łączy potrzeby twórców i firm.",
        brandBtn: "Zacznij jako Marka",
        creatorBtn: "Dołącz jako Twórca",
      },
    },
    howItWorks: {
      title: "Znajdź idealne dopasowanie między Twoją marką a odbiorcami",
      step1Title: "Dogłębna Analiza Persony",
      step1Description:
        "Analizujemy transkrypcje, artykuły, filmy i materiały, aby odkryć kluczowe narracje, podstawowe ramy oraz główne motywacje, które kształtują Twój świat.",
      step2Title: "Kompleksowy Raport Marki",
      step2Description:
        "Tworzymy Raport Persony, który określa charakterystyczny ton, perspektywę i długoterminowe cele Twojej marki — stanowiący fundament do budowania autentycznych i spójnych treści.",
      step3Title: "Dopasowanie AI i Automatyzacja Treści",
      step3Description:
        "Wykorzystując raport, nasz system automatycznie dopasowuje markę do najbardziej odpowiednich mikroinfluencerów do kampanii UGC i generuje zautomatyzowane, zgodne z marką treści.",
      closing:
        "W przeciwieństwie do typowych treści generowanych przez AI, nasze rozwiązanie tworzy treści nie do odróżnienia od tego, co Twoja firma już tworzy — autentyczne, zgodne z marką i skalowalne.",
      comparisonTitle: "Dlaczego UGC z Mikro-Influencerami jest lepsze?",
      authenticity: "Autentyczność i Zaufanie",
      authenticityDesc:
        "Mikro-influencerzy utrzymują bliższe relacje ze swoją publicznością, co skutkuje bardziej autentycznymi rekomendacjami, które brzmią jak rada od przyjaciela, a nie płatna promocja.",
      niche: "Odpowiednia Nisza",
      nicheDesc:
        "Mikro-influencerzy często specjalizują się w konkretnych niszach, co pozwala markom dotrzeć do wysoko targetowanych odbiorców z odpowiednimi zainteresowaniami zamiast szerokiego, nieukierunkowanego zasięgu.",
      budget: "Efektywne Zarządzanie Budżetem",
      budgetDesc:
        "Zamiast wydawać cały budżet na jednego celebrytę-influencera, pracuj z wieloma mikro-influencerami, aby zdywersyfikować ryzyko, przetestować różne rynki i osiągnąć lepszy ogólny ROI.",
      engagement: "Prawdziwe Zaangażowanie vs Puste Liczby",
      engagementDesc:
        "Makro-influencerzy często mają zawyżoną liczbę obserwujących przy niskim zaangażowaniu. Mikro-influencerzy dostarczają rzeczywiste rozmowy, komentarze i konwersje od naprawdę zainteresowanych odbiorców.",
      viral: "POTENCJAŁ NA VIRAL!",
      viralDesc:
        "Autentyczne treści od mikro-twórców mogą stać się viralem tak samo jak treści dużych influencerów, wzmacniając zasięg Twojej marki i tworząc momentum wzrostu.",
    },
    creators: {
      heroTitle: "Monetyzuj swoją kreatywność. Regularnie.",
      heroDescription: "Dołącz do ekosystemu Zendizz. Dopasujemy Cię do marek pasujących do Twojego stylu dzięki AI lub zatrudnimy jako stałą twarz dla naszych klientów.",
      heroButton: "Aplikuj do Bazy Twórców",
      whyZendizz: "Dlaczego Zendizz?",
      whySubtitle: "Koniec ze słabymi barterami i chaosem w DM.",
      valueProps: {
        matchTitle: "Dopasowanie przez Deep Persona Analysis",
        matchDesc: "Nasz algorytm analizuje Twój styl i widownię. Otrzymujesz tylko zlecenia, które naprawdę \"czujesz\". Żadnego promowania steków, jeśli jesteś weganinem.",
        pathsTitle: "Dwie Ścieżki Kariery",
        pathsDesc: "Wybierz między pojedynczymi zleceniami UGC a stałym kontraktem jako Dedykowany Twórca, gdzie prowadzisz profil marki za stałą miesięczną stawkę.",
        rulesTitle: "Jasne Zasady",
        rulesDesc: "Jasne briefy, płatności na czas i wsparcie agencji. Negocjujemy uczciwe stawki z klientem, więc możesz skupić się na tworzeniu."
      },
      chooseMode: "Wybierz Swój Tryb",
      modeSubtitle: "Jak chcesz pracować?",
      modes: {
        ugc: {
          title: "Współpraca Projektowa (Kampanie UGC)",
          subtitle: "Dla twórców szukających elastyczności i różnorodności projektów.",
          targetProfile: "Twórcy ceniący różnorodność i posiadający zaangażowaną społeczność.",
          coreDeliverable: "Produkcja wysokiej jakości, autentycznych treści wideo na własne kanały lub do mediów płatnych/organicznych klienta.",
          compensationModel: "Wynagrodzenie za projekt (pay-per-asset) z możliwością otrzymania produktów.",
          button: "Aplikuj do Kampanii"
        },
        dedicated: {
          title: "Ambasador Marki (Dedykowany Twórca)",
          subtitle: "Dla twórców, którzy stawiają na stabilność i profesjonalną separację marki osobistej.",
          targetProfile: "Twórcy ceniący stabilny, przewidywalny miesięczny dochód, chcący oddzielić pracę komercyjną od marki osobistej.",
          coreDeliverable: "Stajesz się reprezentantem i twarzą marki — budujesz jej wizerunek przed kamerą poprzez regularne treści na kanałach klienta.",
          compensationModel: "Gwarantowane miesięczne wynagrodzenie (retainer) i stabilność długoterminowego kontraktu.",
          button: "Aplikuj na Stanowisko Retainer"
        }
      },
      requirements: {
        title: "Kogo szukamy?",
        subtitle: "Nie patrzymy tylko na liczby. Szukamy Talentu. Nie potrzebujesz miliona obserwujących. Szukamy twórców, którzy:",
        list: [
          "Potrafią opowiadać historie (storytelling) i utrzymać uwagę.",
          "Rozumieją estetykę TikTok/Reels (montaż, oświetlenie, tempo).",
          "Są punktualni (to kluczowe dla nas w B2B).",
          "Są autentyczni w konkretnych niszach (Uroda, Tech, Finanse, Rodzicielstwo, Gaming i inne)."
        ]
      },
      faq: {
        title: "FAQ",
        subtitle: "Krótko i na temat.",
        list: [
          {
            q: "Czy zlecenia są płatne?",
            a: "Tak. Skupiamy się głównie na płatnych kampaniach. Barter to tylko dodatek przy mniejszych zleceniach na start."
          },
          {
            q: "Czy muszę pokazywać twarz?",
            a: "W 90% przypadków tak – to podstawa zaufania w UGC."
          },
          {
            q: "Czym jest Deep Persona Analysis?",
            a: "To nasza autorska technologia, która sprawdza Twoją niszę, demografię i zaangażowanie widowni, aby dopasować Cię tylko do marek, gdzie dostarczysz wysokie wyniki (i wszyscy będą zadowoleni ;))."
          }
        ]
      },
      generalTalentPool: {
        title: "Otwarty/a na Oba Warianty?",
        description: "Jeśli jesteś elastyczny/a w wyborze między kampaniami projektowymi a długoterminowym kontraktem retainer, aplikuj do naszego General Talent Pool. Przeanalizujemy Twój profil i dopasujemy Cię do opcji, która najbardziej pasuje do aktualnego zapotrzebowania klientów.",
        button: "Aplikuj do nas"
      },
    },
    companies: {
      title: "Skaluj zaufanie. Automatyzuj zasięgi.",
      subtitle: "Nie jesteśmy kolejną agencją influencer marketingu, która wyśle Ci listę przypadkowych osób. Jesteśmy partnerem technologicznym. Budujemy dla Ciebie autonomiczne kanały dotarcia do klienta.",
      chooseStrategy: "Wybierz strategię dopasowaną do Twojego etapu rozwoju:",
      getStarted: "Umów spotkanie",
      coreProcess: {
        title: "Od Danych do Dominacji.",
        subtitle: "Nie zgadujemy. Nasz proces zamienia analitykę w content, który konwertuje.",
        steps: [
          {
            title: "Dogłębna Analiza Persony (The Brain)",
            description: "Analizujemy transkrypcje, artykuły, filmy i materiały konkurencji, aby odkryć kluczowe narracje, ramy myślowe oraz główne motywacje, które kształtują świat Twojego klienta. Rozumiemy \"dlaczego\" kupują, zanim zaczniemy sprzedawać."
          },
          {
            title: "Kompleksowy Raport Marki (The Strategy)",
            description: "Tworzymy \"Raport Persony\", który definiuje unikalny ton (Tone of Voice), perspektywę i cele Twojej marki. To fundament, dzięki któremu każde wideo i każdy post – niezależnie czy stworzony przez człowieka, czy AI – jest spójny z Twoim DNA."
          },
          {
            title: "Dopasowanie AI i Automatyzacja (The Execution)",
            description: "Wykorzystując raport, system automatycznie dobiera idealnych twórców LUB generuje autonomiczne treści. AI Content: Tworzymy treści nie do odróżnienia od pracy senior copywritera – autentyczne, zgodne z marką i nieskończenie skalowalne. Human Match: Łączymy Cię z mikro-influencerami, którzy naturalnie pasują do wyników analizy."
          }
        ]
      },
      growthEngines: {
        title: "Jeden ekosystem. Trzy sposoby na wzrost.",
        subtitle: "Wybierz narzędzie dopasowane do Twoich celów.",
        engines: [
          {
            title: "AI Autonomous Growth",
            subtitle: "Dla firm pragnących skali i autorytetu.",
            description: "Zdominuj swoją niszę informacyjną. Nasze systemy generują i publikują slajdy, artykuły, wątki na X i posty na LinkedIn. Budujesz pozycję eksperta 24/7, bez angażowania swojego czasu.",
            benefit: "Skalowalność i omnipresence (obecność wszędzie)."
          },
          {
            title: "Campaign UGC",
            subtitle: "Dla firm potrzebujących zaufania i materiałów reklamowych.",
            description: "Autentyczne wideo od zweryfikowanych mikro-influencerów. Idealne paliwo do Twoich kampanii płatnych (Ads) oraz na social media, by budować dowód społeczny (Social Proof).",
            benefit: "Wysoka konwersja i różnorodność twarzy."
          },
          {
            title: "Dedicated Creators",
            subtitle: "Dla firm szukających stabilności i \"Twarzy Marki\".",
            description: "Zamiast jednorazowych współpracy – stały twórca na wyłączność (Retainer). Buduje relację z Twoją społecznością na Twoich kanałach, zapewniając spójność komunikacji przez długie miesiące.",
            benefit: "Lojalność odbiorców i przewidywalność."
          }
        ]
      },
      whyItWorks: {
        title: "Dlaczego autentyczność wygrywa z zasięgiem?",
        subtitle: "Niezależnie czy wybierzesz AI, czy Ludzi – zasady gry są te same.",
        points: [
          {
            title: "Autentyczność i Zaufanie",
            description: "Mikro-komunikacja buduje bliższe relacje. Treści (czy to wideo twórcy, czy wątek ekspercki AI) brzmią jak rada od partnera, a nie korporacyjny komunikat."
          },
          {
            title: "Odpowiednia Nisza",
            description: "Dzięki Deep Persona Analysis docieramy tylko do wysoko targetowanych odbiorców. Nie płacisz za puste wyświetlenia, ale za uwagę potencjalnych klientów."
          },
          {
            title: "Efektywne Zarządzanie Budżetem",
            description: "Zamiast przepalać budżet na jednego drogiego celebrytę lub agencję PR, dywersyfikujesz działania. Testujesz różne rynki i narracje, osiągając lepszy ogólny ROI."
          },
          {
            title: "Prawdziwe Zaangażowanie vs Puste Liczby",
            description: "Koniec z pompowaniem ego. Dostarczamy rzeczywiste rozmowy, komentarze i konwersje od naprawdę zainteresowanych odbiorców."
          },
          {
            title: "Potencjał na Viral",
            description: "Dobre treści bronią się same. Autentyczny przekaz ma szansę stać się viralem niezależnie od wielkości konta, tworząc darmowe momentum wzrostu dla Twojej marki."
          }
        ]
      },
      packages: {
        humanTrust: {
          title: "Real Influence",
          tagline: "Buduj wiarygodność poprzez autentyczne partnerstwa z twórcami i materiały wideo, które zwiększają współczynnik konwersji.",
          description: "Zakres działań:",
          features: [
            "Data-Driven Creator Matching: Dobór twórców na podstawie twardych danych demograficznych i analizy niszy, a nie \"wyczucia\".",
            "High-Fidelity Video Production: Realizacja materiałów wideo (Reels/TikTok/Shorts) o wysokim potencjale konwersji, gotowych do emisji.",
            {
              title: "Dwie ścieżki egzekucji:",
              subFeatures: [
                "Scalable Campaigns: Jednorazowe, szerokie kampanie z jednym lub wieloma twórcami w celu dywersyfikacji przekazu i testowania kątów reklamowych.",
                "Dedicated Creators (Retainer): Przydzielenie ambasadora/ów marki na wyłączność, który prowadzi spójną komunikację i staje się \"twarzą\" firmy w długim terminie."
              ]
            },
            "Full Commercial Rights: Pełne przekazanie praw autorskich i licencji do wykorzystania w reklamach (Ads).",
            "Performance Tracking: Monitorowanie skuteczności materiałów i raportowanie kluczowych metryk.",
            "Strategic Onboarding: Dedykowany proces wdrożenia i stała opieka Account Managera."
          ],
          idealFor: "E-commerce, marki D2C, aplikacje mobilne.",
          pricingCategories: [
            {
              category: "Campaign UGC",
              options: [
                { label: "Twórcy UGC", price: "od $299" }
              ]
            },
            {
              category: "Dedicated Creators",
              options: [
                { label: "Dedykowani Twórcy UGC", price: "od $699" }
              ]
            }
          ],
          button: "Wybierz"
        },
        aiGrowth: {
          title: "Autonomous Content Ecosystem (AI)",
          tagline: "Rozwiązanie dla firm dążących do omnikanałowej obecności i budowania pozycji lidera opinii (Thought Leader), przy minimalnym zaangażowaniu czasowym.",
          description: "W pakiecie otrzymujesz:",
          features: [
            "AI-Driven Authentic Content: Generowanie merytorycznych treści (slajdy edukacyjne, wątki na X, artykuły na LinkedIn/Blog) zachowujących autentyczność i spójność z Tone of Voice marki. (4-12 treści na tydzień)",
            "Omnichannel Distribution: Jednoczesna publikacja i dystrybucja treści na wszystkich kluczowych platformach.",
            "Strategic Community Engagement: Nadzorowany system interakcji z branżowymi kontami w celu organicznego pozyskiwania ruchu.",
            "Niche Theme Pages: Opcjonalne tworzenie i zarządzanie zewnętrznymi profilami tematycznymi, budującymi rynek wokół Twojej niszy (bezpośrednie wsparcie sprzedaży).",
            "Continuous Optimization: Stała kalibracja strategii treści na podstawie spływających danych o zaangażowaniu."
          ],
          goal: "Buduj autorytet, edukuj rynek, napędzaj organiczny wzrost.",
          idealFor: "SaaS, B2B, usługi profesjonalne, fintech.",
          pricingCategories: [
            {
              category: "Cennik",
              options: [
                { label: "Pakiet Presence (Codzienne posty na 1 platformie)", price: "od $399" },
                { label: "Pakiet Authority (Omnichannel + Blog + Engagement)", price: "od $1599" }
              ]
            }
          ],
          button: "Wybierz"
        },
        hyperGrowth: {
          title: "Holistic Growth Hack (Hybrid)",
          tagline: "Kompletne rozwiązanie: ludzka autentyczność spotyka skalę AI dla dominacji rynkowej.",
          description: "Wszystko z Pakietu 1 i 2, plus:",
          features: [
            "Pełna Synergia Human + AI: Zintegrowana strategia wykorzystująca wideo do budowania relacji oraz treści AI do edukacji rynku i SEO.",
            "Advanced Data Optimization: Ciągłe testy A/B i optymalizacja strategii w czasie rzeczywistym (Real-Time Data).",
            "External Marketing Department: Przejęcie pełnej odpowiedzialności za procesy twórcze i dystrybucyjne.",
            "Priority Support: Dedykowany Project Manager dostępny priorytetowo dla klienta.",
            "Comprehensive ROI Reporting: Zaawansowana analityka konwersji z podziałem na kanały i formaty."
          ],
          goal: "Twój w pełni outsourcowany dział marketingu.",
          idealFor: "Firmy w fazie wzrostu gotowe na liderstwo rynkowe.",
          pricingCategories: [
            {
              category: "Cennik",
              options: [
                { label: "Hyper Growth (Umowa długoterminowa)", price: "Wycena indywidualna" }
              ]
            }
          ],
          button: "Wybierz"
        }
      },
      process: {
        title: "Jak to zrobimy?",
        steps: [
          { title: "Brief & Analiza", description: "Wypełniasz ankietę, my uruchamiamy Deep Persona Analysis." },
          { title: "Matching / Setup", description: "Dobieramy Twórcę (Human) lub konfigurujemy AI." },
          { title: "Review", description: "Akceptujesz strategię i pierwszą paczkę materiałów." },
          { title: "Launch", description: "Startujemy. Ty otrzymujesz gotowe pliki lub my publikujemy je za Ciebie." },
          { title: "Scale", description: "Analizujemy wyniki i dociskamy to, co działa." }
        ]
      },
      faq: {
        title: "Najczęściej Zadawane Pytania",
        list: [
          { q: "Czy mam kontrolę nad tym, co publikują twórcy?", a: "Tak. W modelu \"Dedicated Creator\" i \"Campaign UGC\" masz pełen wgląd w materiały przed ich publikacją. Nic nie wychodzi bez Twojej akceptacji (chyba że dasz nam wolną rękę)." },
          { q: "Czym różni się \"Dedicated Creator\" od zwykłego influencera?", a: "Influencer promuje Ciebie na swoim kanale (obok 10 innych marek). Dedicated Creator tworzy content dla Ciebie, na Twoje kanały. Pracuje jak Twój wirtualny pracownik na stałym kontrakcie." },
          { q: "Czy treści AI są bezpieczne dla wizerunku?", a: "Tak. Nasze AI nie działa samopas. Każda strategia jest kalibrowana przez człowieka, a treści przechodzą weryfikację zgodności z tonem marki (Tone of Voice)." }
        ]
      },
      cta: {
        title: "Gotowy na marketing, który działa?",
        description: "Nie sprzedajemy \"postów\". Sprzedajemy system pozyskiwania klientów. Skonsultuj z nami swoją niszę.",
        button: "Umów spotkanie"
      },
      ugcDialog: {
        title: "Dlaczego UGC i mikro-influencerzy?",
        intro:
          "Treści generowane przez użytkowników (UGC) i mikro-influencerzy rewolucjonizują marketing cyfrowy. Oto dlaczego są bardziej efektywni niż tradycyjny marketing influencerski:",
        authenticity: "Autentyczność nad zasięgiem",
        authenticityDesc: "Mniejsi twórcy są bardziej autentyczni. Ich publiczność ufa im bardziej niż celebrytom.",
        engagement: "Wyższe zaangażowanie",
        engagementDesc: "Mikro-influencerzy często generują 3-5x więcej interakcji w porównaniu z dużymi nazwiskami.",
        roi: "Lepszy ROI",
        roiDesc:
          "Zamiast płacić jedną dużą kwotę, dostajesz dziesiątki autentycznych treści, które przynoszą konwersje.",
        scalable: "Skalowalna i różnorodna treść",
        scalableDesc: "Więcej twórców = więcej stylów, więcej głosów, więcej zaufania.",
        niche: "Targetowanie niszy",
        nicheDesc:
          "Mali twórcy mówią bezpośrednio do zwartych społeczności (fitness, gaming, lifestyle), więc Twoja marka trafia dokładnie tam, gdzie powinna.",
        viral: "Potencjał wiralowy",
        viralDesc:
          "Autentyczne treści małych twórców mogą stać się viralem tak samo jak treści dużych influencerów, wzmacniając zasięg Twojej marki.",
        whyZendizz: "Dlaczego Zendizz?",
        reviewedCreators: "Sprawdzeni i Sklasyfikowani Twórcy:",
        reviewedCreatorsDesc: "Każdy twórca w naszej sieci jest weryfikowany i kategoryzowany dla zapewnienia jakości.",
        perfectMatching: "Idealne Dopasowanie:",
        perfectMatchingDesc: "Nasz algorytm łączy Cię z twórcami, których publiczność idealnie pasuje do Twojej marki.",
        endToEnd: "Kompleksowe Zarządzanie:",
        endToEndDesc: "Od wyboru twórcy po dostarczenie treści i śledzenie wyników, my zajmujemy się wszystkim.",
        getStartedUgc: "Zacznij z UGC",
      },
      ugc: {
        title: "Treści Użytkowników i Marketing Influencerów",
        description: "Kampanie UGC i współpraca z twórcami",
        detail1: "Autentyczne treści użytkowników",
        detail2: "Strategiczne partnerstwa z influencerami",
        detail3: "Opowiadanie historii marki na dużą skalę",
        button: "Rozpocznij Kampanię UGC",
        specialization: "NASZA SPECJALIZACJA",
      },
      aiContent: {
        title: "Treści AI",
        description: "Szybka produkcja wideo/obrazów AI z edycją",
        detail1: "Tworzenie wideo napędzane AI",
        detail2: "Inteligentne generowanie obrazów",
        detail3: "Automatyczna edycja treści",
        button: "Generuj Treści AI",
      },
      performance: {
        title: "Performance Marketing",
        description: "Reklamy TikTok/Meta/Google, SEO/SEM",
        detail1: "Kampanie reklamowe wieloplatformowe",
        detail2: "Optymalizacja pod kątem ROI",
        detail3: "Marketing w wyszukiwarkach",
        button: "Uruchom Marketing",
      },
      automation: {
        title: "Automatyzacja AI",
        description: "Automatyzacje Make/Zapier, integracje, dashboardy",
        detail1: "Automatyzacja przepływów pracy",
        detail2: "Integracje systemów",
        detail3: "Niestandardowe dashboardy",
        button: "Automatyzuj Procesy",
      },
      intelligence: {
        title: "Badanie Rynku",
        description: "Raporty, spostrzeżenia, analiza konkurencji i trendów",
        detail1: "Analiza konkurencji",
        detail2: "Raporty trendów rynkowych",
        detail3: "Strategiczne spostrzeżenia",
        button: "Uzyskaj Analizy",
      },
      productStrategy: {
        title: "Strategia Produktowa",
        description: "Projektowanie oferty, copywriting, ceny i marże",
        detail1: "Pozycjonowanie produktu",
        detail2: "Optymalizacja cen",
        detail3: "Planowanie strategiczne",
        button: "Optymalizuj Strategię",
      },
      extraServices: "Dodatkowe Usługi",
      websites: {
        title: "Strony WWW i E-commerce",
        description: "Sklepy Shopify lub niestandardowe strony z integracjami marketingowymi",
        detail1: "Niestandardowe tworzenie stron",
        detail2: "Rozwiązania e-commerce",
        detail3: "Integracje marketingowe",
        button: "Zbuduj Stronę",
      },
      growthExp: {
        title: "Rozwój i Eksperymenty",
        description: "Analiza lejków, testy A/B, automatyzacja, retencja",
        detail1: "Optymalizacja konwersji",
        detail2: "Framework testów A/B",
        detail3: "Automatyzacja wzrostu",
        button: "Rozpocznij Testowanie",
      },
      whyChoose: "Dlaczego Zendizz?",
      lightningFast: "Błyskawicznie Szybko",
      lightningFastDesc: "Szybkie wdrożenie i wykonanie.",
      provenResults: "Sprawdzone Rezultaty",
      provenResultsDesc: "Strategie oparte na danych, które przynoszą efekty.",
      aiPowered: "Napędzane AI",
      aiPoweredDesc: "Najnowocześniejsze rozwiązania technologiczne.",
      expertTeam: "Zespół Ekspertów",
      expertTeamDesc: "Dedykowani profesjonaliści dla Twojego sukcesu.",
      howItWorks: "Jak to Działa",
      consultation: "Konsultacja",
      consultationDesc: "Rozumiemy cele Twojego biznesu.",
      strategy: "Strategia",
      strategyDesc: "Niestandardowe rozwiązania dopasowane do Ciebie.",
      growth: "Rozwój",
      growthDesc: "Obserwuj, jak Twój biznes rośnie i odnosi sukces.",
    },
    about: {
      title: "O Nas",
      intro:
        "W Zendizz łączymy twórców z markami poprzez inteligentne dopasowanie napędzane sztuczną inteligencją. Nasza platforma została stworzona, by łączyć mikroinfluencerów z firmami poszukującymi autentycznych, angażujących treści, tworząc partnerstwa, które realnie napędzają wzrost po obu stronach.",
      content1:
        "Nie jesteśmy kolejnym marketplace'em. Zendizz to kompleksowy ekosystem, w którym kampanie UGC spotykają się z zaawansowaną technologią AI, performance marketingiem i strategicznym rozwojem marki. Niezależnie od tego, czy chodzi o dopasowanie twórców do właściwych marek, skalowanie zasięgu dzięki autentycznym partnerstwom, czy automatyczne generowanie treści nieodróżnialnych od tych tworzonych przez was do tej pory— sprawiamy, że wzrost staje się płynny, mierzalny i trwały.",
      content2:
        "Nasza technologia AI analizuje profile twórców, dopasowuje ich do marek o zbieżnych wartościach i odbiorcach oraz optymalizuje każdą współpracę i generowaną treść dla maksymalnego efektu.",
      content3:
        "Dla marek oznacza to dostęp do twórców i treści, które naprawdę rezonują z ich przekazem. Dla twórców — możliwości, które idealnie wpisują się w ich niszę i wspierają rozwój kariery.",
      content4:
        "W Zendizz nie tylko ułatwiamy współprace — budujemy społeczność, w której marki i twórcy rosną razem, wspierani przez technologię, która sprawia, że każde partnerstwo i każda treść są mądrzejsze, bardziej autentyczne i efektywne.",
      quote: "Pomagamy twórcom i markom rosnąć razem — jako jedna, spójna całość.",
      founderName: "Blaze, Założyciel Zendizz",
      certification: "Certyfikowany w AI dla Rozwoju Biznesu",
      coreValues: "Nasze Główne Wartości",
      authenticity: "Autentyczność",
      authenticityDesc: "Wierzymy w autentyczne połączenia i prawdziwe relacje między twórcami a markami.",
      innovation: "Innowacja",
      innovationDesc:
        "Wykorzystujemy AI i najnowocześniejsze technologie do tworzenia mądrzejszych, bardziej efektywnych kampanii.",
      growth: "Rozwój",
      growthDesc: "Poświęceni przyspieszaniu rezultatów i pomaganiu naszym partnerom w szybszym skalowaniu.",
    },
    portfolio: {
      title: "Nasze Prace",
      subtitle: "Od kampanii UGC po pełną transformację cyfrową — zobacz, co tworzymy dla twórców i marek.",
      loading: "Ładowanie pozycji portfolio...",
      ctaTitle: "Gotowy na Stworzenie Czegoś Niesamowitego?",
      ctaSubtitle: "Współpracujmy i wcielmy w życie Twoją wizję dzięki naszym kompleksowym rozwiązaniom marketingowym.",
      ctaButton: "Rozpocznij Swój Projekt",
      sections: {
        ugc: {
          title: "Kampanie UGC i Influencer",
          description: "Autentyczne współprace z twórcami, które napędzają prawdziwe zaangażowanie i konwersje.",
        },
        aiContent: {
          title: "Produkcja Treści Napędzana AI",
          description: "Najnowocześniejsze tworzenie treści dzięki sztucznej inteligencji.",
        },
        webDesign: {
          title: "Projektowanie i Rozwój Stron WWW",
          description: "Piękne, wysoko konwertujące strony internetowe zbudowane z nowoczesną technologią.",
        },
        ecommerce: {
          title: "Rozwiązania E-Commerce",
          description: "Kompletne sklepy internetowe maksymalizujące sprzedaż i doświadczenie klienta.",
        },
        performance: {
          title: "Performance Marketing",
          description: "Kampanie oparte na danych dostarczające mierzalny ROI.",
        },
        brand: {
          title: "Strategia i Pozycjonowanie Marki",
          description: "Strategiczne wsparcie pomagające markom wyróżnić się i skalować.",
        },
      },
    },
    creatorForm: {
      title: "Aplikacja dla Twórcy",
      subtitle: "Wypełnij aplikację, postępując zgodnie z poniższymi krokami.",
      step1Title: "Wybierz Swoje Platformy",
      step1Subtitle: "Wybierz platformy, na których tworzysz treści",
      step2Title: "Kategorie Treści",
      step2Subtitle: "Wybierz 1-5 kategorii opisujących Twoje treści",
      step3Title: "Informacje Kontaktowe",
      step3Subtitle: "Powiedz nam, jak się z Tobą skontaktować",
      platformsSectionTitle: "Wybierz Swoje Platformy",
      platformsSectionDesc: "Wybierz platformy, na których jesteś najbardziej aktywny.",
      verificationSectionTitle: "Weryfikacja",
      verificationSectionDesc: "Ukończ weryfikację, aby przesłać zgłoszenie.",
      selectPlatform: "Wybierz przynajmniej jedną platformę",
      followers: "Obserwujący",
      averageViewers: "Średnia Widzów",
      platformLink: "Link do Platformy (opcjonalnie)",
      name: "Pełne Imię i Nazwisko",
      email: "E-mail",
      country: "Kraj",
      selectCountry: "Wybierz swój kraj",
      other: "Inne",
      maxCategories: "Wybierz maksymalnie 5 kategorii",
      categoryOther: "Określ inną kategorię",
      back: "Wstecz",
      next: "Dalej",
      submit: "Wyślij Aplikację",
      submitting: "Wysyłanie...",
      qualified: "Gratulacje!",
      qualifiedMessage: "Dziękujemy! Kwalifikujesz się do dalszej oceny. Będziemy w kontakcie!",
      notQualified: "Rozwijaj się dalej!",
      notQualifiedMessage:
        "System ocenił, że na razie nie spełniasz wszystkich naszych kryteriów. Bez obaw — każde zgłoszenie sprawdzamy także ręcznie, więc nic nie jest przesądzone ;)",
      close: "Zamknij",
      errorPlatform: "Wybierz przynajmniej jedną platformę",
      errorCategories: "Wybierz przynajmniej 1 kategorię treści",
      errorMaxCategories: "Wybierz nie więcej niż 5 kategorii treści",
      errorContactInfo: "Podaj imię, e-mail i kraj",
      errorEmail: "Podaj prawidłowy adres e-mail",
    },
    companyForm: {
      title: "Zapytanie o Usługę",
      step1Title: "Jaki jest Twój główny cel?",
      step1Subtitle: "Wybierz główny cel tego projektu",
      step2Title: "Wybierz swój pakiet",
      step2Subtitle: "Wybierz usługę, która najlepiej odpowiada Twoim potrzebom",
      step3Title: "Informacje Kontaktowe",
      step3Subtitle: "Powiedz nam o swojej firmie",
      goals: {
        createContent: "Tworzyć treści",
        workCreators: "Współpracować z twórcami",
        scalePerformance: "Skalować wyniki",
        automateIntegrate: "Automatyzować i integrować",
        understandMarket: "Zrozumieć rynek",
        buildWebsite: "Zbudować stronę / sklep",
      },
      packages: {
        aiContent: "Treści AI",
        ugcInfluencers: "Treści Użytkowników i Marketing Influencerów",
        performanceMarketing: "Performance Marketing",
        websitesEcommerce: "Strony WWW i E-commerce",
        aiAutomation: "Automatyzacja AI",
        marketIntelligence: "Badanie Rynku",
        ugcPackage: "UGC lub Influencer marketing",
        ugcPackageDesc:
          "Współpracuj z autentycznymi twórcami, którzy ożywiają Twoją markę. Łączymy Cię ze sprawdzonymi twórcami treści i influencerami specjalizującymi się w treściach generowanych przez użytkowników, które rezonują z Twoją grupą docelową.",
        aiAutomationPackage: "Automatyzacja Marketingu AI",
        aiAutomationPackageDesc:
          "Przekształć swój marketing dzięki inteligentnej automatyzacji opartej na najnowocześniejszej technologii AI. Budujemy niestandardowe rozwiązania AI, które generują wysokiej jakości treści na skalę, automatyzują powtarzalne zadania marketingowe i personalizują doświadczenia klientów.",
        fullPackage: "Hyper Growth",
        fullPackageDesc:
          "Uzyskaj najlepszy silnik wzrostu łączący autentyczność partnerstw z twórcami z efektywnością automatyzacji AI. To kompleksowe rozwiązanie daje Ci dostęp do całej naszej sieci twórców oraz niestandardowych narzędzi AI.",
      },
      step1ChoosePath: "Wybierz Swoją Ścieżkę Wzrostu",
      step1SelectPackage: "Wybierz idealny pakiet, aby przyspieszyć rozwój Twojego biznesu",
      getStarted: "Rozpocznij",
      collaborationType: "Typ współpracy",
      ugcLabel: "User Generated Content (UGC)",
      ugcDescription: "Krótkoterminowe kampanie tworzenia treści z autentycznymi treściami użytkowników",
      influencerLabel: "Długoterminowa Współpraca z Influencerem",
      influencerDescription: "Ciągłe partnerstwa z twórcami treści reprezentującymi markę",
      selectCategories: "Wybierz kategorie treści",
      selectPlatforms: "Wybierz platformy",
      selectCountry: "Wybierz swój kraj",
      fullName: "Pełne Imię i Nazwisko",
      companyName: "Nazwa Firmy",
      email: "E-mail",
      website: "Strona WWW (opcjonalnie)",
      projectDescription: "Opis Projektu (opcjonalnie)",
      estimatedBudget: "Szacowany Budżet (opcjonalnie)",
      addGrowthExp: "Dodaj Rozwój i Eksperymenty",
      back: "Wstecz",
      next: "Dalej",
      submit: "Wyślij Zapytanie",
      submitting: "Wysyłanie...",
      successTitle: "Aplikacja wysłana pomyślnie!",
      successMessage: "Skontaktujemy się z Tobą wkrótce.",
      matchingCreators: "Znaleźliśmy {count} wykwalifikowanych twórców pasujących do Twoich kryteriów!",
      ugcInfoTitle: "Dlaczego UGC z Małymi Twórcami Działa Lepiej niż z Dużymi Influencerami",
      continueButton: "Przejdź do Aplikacji",
      ugcBenefits: {
        authenticity: "Autentyczność nad zasięgiem",
        authenticityDesc: "Mniejsi twórcy są bardziej autentyczni. Ich publiczność ufa im bardziej niż celebrytom.",
        engagement: "Wyższe zaangażowanie",
        engagementDesc: "Mikro-influencerzy często generują 3-5x więcej interakcji w porównaniu z dużymi nazwiskami.",
        roi: "Lepszy ROI",
        roiDesc:
          "Zamiast płacić jedną dużą kwotę, dostajesz dziesiątki autentycznych treści, które przynoszą konwersje.",
        scalable: "Skalowalna i różnorodna treść",
        scalableDesc: "Więcej twórców = więcej stylów, więcej głosów, więcej zaufania.",
        niche: "Targetowanie niszy",
        nicheDesc:
          "Mali twórcy mówią bezpośrednio do zwartych społeczności (fitness, gaming, lifestyle), więc Twoja marka trafia dokładnie tam, gdzie powinna.",
        viral: "Potencjał wiralowy",
        viralDesc:
          "Autentyczne treści małych twórców mogą stać się viralem tak samo jak treści dużych influencerów, wzmacniając zasięg Twojej marki.",
        conclusion: "👉 UGC z mikro-influencerami to nie tylko treść. To konwersja.",
      },
      creatorCollabTitle: "Współpraca z Twórcami",
      creatorCollabDesc: "Powiedz nam, jakiego typu twórców szukasz",
      collaborationTypeTitle: "Typ współpracy",
      collaborationTypeDesc: "Wybierz typ współpracy, który odpowiada Twoim potrzebom",
      ugcCollabDesc: "Krótkoterminowe kampanie pokazujące produkt lub usługę z perspektywy klienta.",
      influencerCollabDesc: "Dzięki regularnym kampaniom marka zyskuje lojalność odbiorców oraz konsekwentny wzrost rozpoznawalności.",
      selectCategoriesDesc: "Wybierz do 5 kategorii treści ({selected}: {count}/5)",
      selectPlatformsTitle: "Wybierz platformy",
      selectPlatformsDesc: "Wybierz platformy, na których chcesz treści",
      cancel: "Anuluj",
      continueToForm: "Przejdź do Formularza",
      primaryGoal: "Jaki jest Twój główny cel?",
      primaryGoalDesc: "Wybierz opcję, która najlepiej opisuje Twój główny cel",
      recommendedPackage: "Rekomendowany pakiet",
      basedOnGoal: "Na podstawie Twojego celu rekomendujemy",
      alternative: "Alternatywa",
      selectPackage: "Wybierz swój pakiet",
      selectPackageDesc: "Wybierz usługę, która najlepiej odpowiada Twoim potrzebom",
      creatorCollabSetup: "Konfiguracja Współpracy z Twórcami",
      findMatchingCreators: "Znajdź Pasujących Twórców",
      nextStep: "Następny Krok",
      letsConnect: "Połączmy się!",
      shareDetails: "Podziel się swoimi danymi, abyśmy mogli dopasować idealne rozwiązanie dla Twojej firmy",
      contactInfo: "Informacje Kontaktowe",
      howReach: "Jak możemy się z Tobą skontaktować?",
      companyInfo: "Informacje o Firmie",
      tellMore: "Powiedz nam więcej o swojej firmie",
      projectDetails: "Szczegóły Projektu",
      projectDetailsDesc: "Pomóż nam zrozumieć Twoje potrzeby (opcjonalnie)",
      describeProject: "Opisz swój projekt",
      projectPlaceholder: "Podziel się swoimi celami, wyzwaniami i tym, co chcesz osiągnąć...",
      estimatedBudgetLabel: "Szacowany Budżet",
      selectBudget: "Wybierz zakres budżetu",
      budgetOption1: "Mniej niż 20,000 zł",
      budgetOption2: "20,000 zł - 60,000 zł",
      budgetOption3: "60,000 zł - 200,000 zł",
      budgetOption4: "200,000+ zł",
      budgetOption5: "Jeszcze nie wiem",
      perfectMatch: "Idealnie! Znaleźliśmy {count} twórców, którzy pasują do Twoich wymagań.",
      expectEmail: "Oczekuj e-maila od nas w ciągu 24 godzin z profilami twórców i kolejnymi krokami.",
      noMatch: "Obecnie nie mamy twórców pasujących do Twoich dokładnych kryteriów, ale nie martw się!",
      activeSearch:
        "Nasz zespół będzie aktywnie szukał twórców pasujących do Twoich potrzeb i skontaktuje się w ciągu 48 godzin z potencjalnymi dopasowaniami.",
      collaboration: "Współpraca",
      categoriesLabel: "Kategorie",
      platforms: "Platformy",
      selected: "Wybrane",
      specifyCategory: "Określ swoją kategorię (maksymalnie 25 słów):",
      describePlaceholder: "Opisz swoją niszową kategorię...",
      words: "słów",
      categories: {
        fitness: "Fitness",
        gaming: "Gaming",
        lifestyle: "Lifestyle",
        fashion: "Moda",
        beauty: "Uroda",
        tech: "Technologia",
        education: "Edukacja",
        finance: "Finanse i Biznes",
        travel: "Podróże",
        food: "Jedzenie i Gotowanie",
        music: "Muzyka",
        art: "Sztuka i Design",
        photography: "Fotografia",
        self_improvement: "Rozwój Osobisty",
        parenting: "Rodzicielstwo i Rodzina",
        pets: "Zwierzęta",
        automotive: "Motoryzacja",
        outdoors: "Outdoor",
        diy: "DIY i Majsterkowanie",
        home: "Dom i Ogród",
        comedy: "Komedia",
        books: "Książki",
        science: "Nauka",
        crypto: "Kryptowaluty i Web3",
        other: "Inne",
      },
    },
    // Toast messages
    toast: {
      logoutSuccess: "Wylogowano pomyślnie",
      applicationSubmitted: "Aplikacja została wysłana pomyślnie!",
      foundCreators: "Znaleziono {count} zakwalifikowanych twórców{plural} spełniających Twoje kryteria!",
      contactSoon: "Skontaktujemy się z Tobą wkrótce.",
      submissionFailed: "Wysłanie nie powiodło się",
      tryAgainLater: "Spróbuj ponownie później.",
    },
    // Success screen messages
    successScreen: {
      submissionSent: "Zgłoszenie Wysłane!",
      thankYou: "Dziękujemy za zgłoszenie. Przejrzymy Twoją prośbę i skontaktujemy się z Tobą w ciągu 24 godzin.",
      submissionFailedTitle: "Wysyłanie nie powiodło się",
      submissionFailedDesc: "Wystąpił błąd podczas wysyłania zgłoszenia. Spróbuj ponownie.",
      tryAgain: "Spróbuj ponownie",
    },
  },
};
